import React, { useEffect, useState, useCallback } from 'react';
import { IconButton, Tooltip } from '@mui/material';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import axios from 'axios';

/**
 * Toggle bookmark for any entity. Backed by /api/user/bookmarks (server-side
 * dedupe on user_id + target_type + target_id), so the same target can be
 * saved at most once per user.
 *
 * Usage:
 *   <BookmarkButton targetType="method" targetId="m_15" label="Transformer" />
 *   <BookmarkButton targetType="author" targetId="a_george" label="Georgia G." />
 *   <BookmarkButton targetType="connection"
 *                   targetId={`${src}|EXTENDS|${tgt}`}
 *                   label={`${srcName} → EXTENDS → ${tgtName}`} />
 */
export default function BookmarkButton({
  targetType,
  targetId,
  label,
  size = 'small',
  sx = {},
}) {
  const [saved, setSaved] = useState(null); // bookmark id when saved
  const [busy, setBusy] = useState(false);

  // Resolve current state — single GET, server returns full list per user.
  const refresh = useCallback(async () => {
    if (!targetType || !targetId) return;
    try {
      const { data } = await axios.get('/api/user/bookmarks');
      const items = (data && data.items) || data || [];
      const hit = items.find(
        (b) => b.target_type === targetType && String(b.target_id) === String(targetId),
      );
      setSaved(hit ? hit.id : null);
    } catch (_) { /* leave unknown */ }
  }, [targetType, targetId]);
  useEffect(() => { refresh(); }, [refresh]);

  const toggle = async (e) => {
    e?.stopPropagation?.();
    if (busy || !targetType || !targetId) return;
    setBusy(true);
    try {
      if (saved) {
        await axios.delete(`/api/user/bookmarks/${saved}`);
        setSaved(null);
      } else {
        const { data } = await axios.post('/api/user/bookmarks', {
          target_type: targetType,
          target_id: String(targetId),
          label: label || null,
        });
        setSaved(data?.id ?? null);
      }
    } catch (err) {
      // Ignore — UI just stays in its previous state. Common: 409 dupe.
      // (We refresh below to reconcile in case the server already had it.)
      refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Tooltip title={saved ? 'Remove from saved' : 'Save to Workspace'}>
      <IconButton
        size={size}
        onClick={toggle}
        disabled={busy}
        sx={{ color: saved ? 'primary.main' : 'text.secondary', ...sx }}
        aria-label={saved ? 'Remove bookmark' : 'Save bookmark'}
      >
        {saved ? <BookmarkIcon fontSize="inherit" /> : <BookmarkBorderIcon fontSize="inherit" />}
      </IconButton>
    </Tooltip>
  );
}
